import { ButtonBase } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import { endOfYear, isAfter, isBefore, isValid, parse, startOfToday, startOfYear } from 'date-fns';
import { FormikProvider, useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import Button from '~components/button';
import { FormikDayPickerInput } from '~components/date-picker';
import FormikRangeDayPickerInput from '~components/date-picker/formik-range-day-picker-input';
import parseValueToDate from '~components/date-picker/helpers/parse-value-to-date';
import { DATE_FORMAT, SPLITTER } from '~components/date-picker/range-day-picker-input';
import FormField from '~components/form-field';
import FormikInput from '~components/input/formik-input';
import FormikRadioGroup from '~components/radio-group/formik-radio-group';
import FormikSwitch from '~components/switch/formik-switch';
import Typography from '~components/typography';
import { TENANCY_RATE_TYPE } from '~graphql-types';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      position: 'absolute',
      left: 0,
      bottom: 0,
      width: '560px',
      height: 424,
      padding: '12px 0 78px',
      backgroundColor: theme.colors.greyFa,
      // There's no predefined boxShadow theme like this
      boxShadow: '0 -2px 16px 0 rgba(51, 53, 50, 0.16)',
    },
    form: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      height: 324,
      overflow: 'scroll',
    },

    field: {
      paddingTop: '16px',
    },

    fieldLabel: {
      marginTop: 0,
      marginBottom: '8px',

      '& > label': {
        ...theme.typography.h6,
        color: theme.colors.dark,
      },
    },

    fieldInput: {
      width: '320px',
      minWidth: '320px',
      mixHeight: '40px',
    },

    fieldInputIntervalDays: {
      marginRight: '12px',
    },

    actions: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingLeft: 120,
      position: 'absolute',
      left: 0,
      bottom: 0,
      width: '100%',
      height: 72,
      background: theme.colors.greyFa,
    },

    actionCancel: {
      ...theme.typography.button,
      marginLeft: '32px',
      color: theme.colors.grey66,
    },

    radiosRateType: {
      height: 24,
    },
  }),
  { name: 'TenancyCustomizedForm' }
);

const getStartDate = (date) => parseValueToDate(date.split(SPLITTER)[0], DATE_FORMAT);

const TenancyCustomizedForm = ({
  currentAcademicYear,
  data,
  onClose,
  onConfirm,
  rateTypeOnPropertySetting,
}) => {
  const { t } = useTranslation(['booking', 'booking-details']);
  const classes = useStyles();

  const rateTypeOnPropertySettingValues = useMemo(
    () => (rateTypeOnPropertySetting || '').split('_AND_'),
    [rateTypeOnPropertySetting]
  );

  const isMonthlyAndNightly = useMemo(() => rateTypeOnPropertySettingValues?.length > 1, [
    rateTypeOnPropertySettingValues?.length,
  ]);

  const validationSchema = yup.object().shape({
    rate_type: yup.string().label(t('rate_type')).required(),
    date_range: yup
      .string()
      .label(t('booking.creating.form.date_range'))
      .required()
      .test(
        'matchDateFormat',
        t('form.validation.validation_error', { fieldName: t('booking.creating.form.date_range') }),
        function testDateRange(value) {
          if (value) {
            const [startDate, endDate] = value.split(SPLITTER);
            const inputDateFormat = 'EEE, d MMM yyyy';
            return (
              isValid(parse(startDate, inputDateFormat, new Date())) &&
              isValid(parse(endDate, inputDateFormat, new Date()))
            );
          }
          return false;
        }
      ),

    charging_date_range: yup
      .string()
      .label(t('booking.creating.form.charging_date_range'))
      .when('isDifferentChargingDate', {
        is: true,
        then: yup
          .string()
          .required()
          .test(
            'matchDateFormat',
            t('form.validation.validation_error', {
              fieldName: t('booking.creating.form.date_range'),
            }),
            function testDateRange(value) {
              if (value) {
                const [startDate, endDate] = value.split(SPLITTER);
                const inputDateFormat = 'EEE, d MMM yyyy';
                return (
                  isValid(parse(startDate, inputDateFormat, new Date())) &&
                  isValid(parse(endDate, inputDateFormat, new Date()))
                );
              }
              return false;
            }
          )
          .test(
            'checkDate',
            t('booking-details:the_charging_date_range_should_be_different'),
            function checkDateRange(value) {
              if (value) {
                const contractDate = formik?.values?.date_range;
                return value !== contractDate;
              }
              return false;
            }
          ),
      }),

    free_cancellation_deadline_date: yup
      .string()
      .label(t('booking.creating.form.free_cancellation_deadline_date'))
      .required(),
    interval_for_turnaround: yup
      .number()
      .integer()
      .min(0)
      .label(t('booking.creating.form.interval_for_turnaround'))
      .required(),
  });

  const initialValues = useMemo(
    () => ({
      rate_type: isMonthlyAndNightly ? TENANCY_RATE_TYPE.NIGHTLY : rateTypeOnPropertySetting,
      date_range: data.date_range,
      free_cancellation_deadline_date: data.free_cancellation_deadline_date,
      interval_for_turnaround: data.interval_for_turnaround || 0,
      isDifferentChargingDate: data.isDifferentChargingDate,
      charging_date_range: data.charging_date_range,
    }),
    [
      data.charging_date_range,
      data.date_range,
      data.free_cancellation_deadline_date,
      data.interval_for_turnaround,
      data.isDifferentChargingDate,
      isMonthlyAndNightly,
      rateTypeOnPropertySetting,
    ]
  );

  const onSubmit = (fields) => onConfirm(fields);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    validateOnBlur: false,
  });
  const { values, setFieldValue } = formik;

  useEffect(() => {
    if (values.date_range && values.free_cancellation_deadline_date) {
      const isAfterDateRange = isAfter(
        parseValueToDate(values.free_cancellation_deadline_date, DATE_FORMAT),
        getStartDate(values.date_range)
      );
      if (isAfterDateRange) {
        setFieldValue('free_cancellation_deadline_date', '', false);
      }

      const today = startOfToday();
      const isAfterToday = isAfter(
        today,
        parseValueToDate(values.free_cancellation_deadline_date, DATE_FORMAT)
      );

      if (
        isAfterToday &&
        formik.values.free_cancellation_deadline_date !== values.date_range.split(SPLITTER)[0]
      ) {
        setFieldValue('free_cancellation_deadline_date', '', false);
      }
    }
  }, [
    values.date_range,
    values.free_cancellation_deadline_date,
    setFieldValue,
    formik.values.free_cancellation_deadline_date,
  ]);

  const rangeDateDisabledDays = useMemo(() => {
    const disabledDays = [];
    if (currentAcademicYear?.fromYear) {
      disabledDays.push({ before: startOfYear(new Date(currentAcademicYear.fromYear, 1)) });
    }
    if (currentAcademicYear?.toYear) {
      disabledDays.push({ after: endOfYear(new Date(currentAcademicYear.toYear, 1)) });
    }
    return disabledDays;
  }, [currentAcademicYear]);

  const freeCancellationDisabledDays = useMemo(() => {
    const disabledDays = [];
    if (values.date_range) {
      const today = startOfToday();
      const dateRangeStart = parseValueToDate(values.date_range.split(SPLITTER)[0], DATE_FORMAT);
      const isBeforeToday = isBefore(today, dateRangeStart);
      disabledDays.push(
        isBeforeToday
          ? {
              before: today,
              after: isBeforeToday ? dateRangeStart : today,
            }
          : {
              before: dateRangeStart,
              after: dateRangeStart,
            }
      );
    }
    return disabledDays;
  }, [values.date_range]);

  const fieldStyle = {
    root: classes.field,
    labelWrapper: classes.fieldLabel,
    inputWrapper: classes.fieldInput,
  };

  return (
    <FormikProvider value={formik}>
      <div className={classes.root}>
        <form onSubmit={formik.handleSubmit} className={classes.form}>
          <FormField
            classes={fieldStyle}
            direction="vertical"
            label={t('booking.creating.form.date_range')}
            inputElements={
              <FormikRangeDayPickerInput
                fieldConfig="date_range"
                DayPickerProps={{ disabledDays: rangeDateDisabledDays }}
                placeholder={t('select_date_range')}
              />
            }
          />

          <FormField
            classes={fieldStyle}
            direction="vertical"
            tooltipTitle={t('booking-details:different_charging_dates.tips')}
            label={t('booking-details:different_charging_dates')}
            inputElements={<FormikSwitch fieldConfig="isDifferentChargingDate" />}
          />

          {!!formik?.values?.isDifferentChargingDate && (
            <FormField
              classes={fieldStyle}
              direction="vertical"
              label={t('booking.creating.form.charging_date_range')}
              inputElements={
                <FormikRangeDayPickerInput
                  fieldConfig="charging_date_range"
                  DayPickerProps={{ disabledDays: rangeDateDisabledDays }}
                  placeholder={t('select_date_range')}
                />
              }
            />
          )}

          <FormField
            classes={fieldStyle}
            direction="vertical"
            label={t('rate_type')}
            inputElements={
              isMonthlyAndNightly ? (
                <FormikRadioGroup
                  className={classes.radiosRateType}
                  fieldConfig="rate_type"
                  radios={Object.keys(TENANCY_RATE_TYPE).map((item) => ({
                    value: item,
                    label: t(`graphql:rate_type.${item}`),
                  }))}
                />
              ) : (
                <Typography variant="body1" themeColor="dark">
                  {t(`graphql:rate_type.${rateTypeOnPropertySetting}`)}
                </Typography>
              )
            }
          />

          <FormField
            classes={fieldStyle}
            direction="vertical"
            label={t('booking.creating.form.interval_for_turnaround')}
            inputElements={
              <FormikInput
                fieldConfig="interval_for_turnaround"
                endAdornment={
                  <Typography
                    variant="body1"
                    themeColor="dark"
                    className={classes.fieldInputIntervalDays}
                  >
                    {t('booking.creating.interval_for_turnaround.days')}
                  </Typography>
                }
              />
            }
          />

          <FormField
            classes={fieldStyle}
            direction="vertical"
            label={t('booking.creating.form.free_cancellation_deadline_date')}
            inputElements={
              <FormikDayPickerInput
                format={DATE_FORMAT}
                fieldConfig="free_cancellation_deadline_date"
                DayPickerProps={{
                  disabledDays: freeCancellationDisabledDays,
                }}
              />
            }
          />

          <div className={classes.actions}>
            <Button type="submit" key="confirm" color="primary">
              {t('button.confirm')}
            </Button>
            <ButtonBase onClick={onClose} className={classes.actionCancel}>
              {t('button.cancel')}
            </ButtonBase>
          </div>
        </form>
      </div>
    </FormikProvider>
  );
};

TenancyCustomizedForm.propTypes = {
  currentAcademicYear: PropTypes.object,
  data: PropTypes.shape({
    date_range: PropTypes.string,
    free_cancellation_deadline_date: PropTypes.string,
    interval_for_turnaround: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    charging_date_range: PropTypes.string,
    isDifferentChargingDate: PropTypes.bool,
  }),
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  rateTypeOnPropertySetting: PropTypes.string,
};

TenancyCustomizedForm.defaultProps = {
  currentAcademicYear: undefined,
  data: {
    date_range: '',
    free_cancellation_deadline_date: '',
    interval_for_turnaround: 0,
    isDifferentChargingDate: false,
    charging_date_range: '',
  },
  onClose: () => {},
  onConfirm: () => {},
  rateTypeOnPropertySetting: '',
};

export default TenancyCustomizedForm;
